/**
 * Phase 9G — single source of truth for rendering renewal/revival surfaces.
 *
 * The backend's `/api/judgment-law/timeline`, `/api/debtors/{id}/checklist`,
 * `/api/renewal/config/{state}` and `/api/monitoring/portfolio-timeline`
 * endpoints all now include a `renewal_display_mode` field with one of:
 *     "YES"               — pro-se renewal available
 *     "ATTORNEY_REQUIRED" — NO pathway, admin showing attorney copy
 *     "NO_PATHWAY"        — NO pathway, admin set display to "no_pathway"
 *     "EXPIRED"           — hard-expired (AZ pre-2013 unrenewed)
 *
 * Components use the helpers below to decide whether to render renewal
 * content, hide it, or replace it with the neutral copy.
 */

export const RENEWAL_MODE = {
  YES: "YES",
  ATTORNEY_REQUIRED: "ATTORNEY_REQUIRED",
  NO_PATHWAY: "NO_PATHWAY",
  EXPIRED: "EXPIRED",
};

/**
 * Returns the renewal display mode from any object that may carry a
 * `renewal_display_mode` field. Falls back to deriving from a resolved
 * `/api/renewal/config` payload (renewal_pathway + no_pathway_display_mode)
 * for defense-in-depth.
 */
export const resolveRenewalDisplay = (source) => {
  if (!source) return null;
  if (source.renewal_display_mode) return source.renewal_display_mode;
  const pathway = source.renewal_pathway;
  if (pathway === "YES") return RENEWAL_MODE.YES;
  if (pathway === "EXPIRED") return RENEWAL_MODE.EXPIRED;
  if (pathway === "NO") {
    return source.no_pathway_display_mode === "no_pathway"
      ? RENEWAL_MODE.NO_PATHWAY
      : RENEWAL_MODE.ATTORNEY_REQUIRED;
  }
  return null;
};

export const isNoPathway = (source) => resolveRenewalDisplay(source) === RENEWAL_MODE.NO_PATHWAY;
export const hasRenewalPathway = (source) => resolveRenewalDisplay(source) === RENEWAL_MODE.YES;

/** Short neutral message rendered everywhere a NO_PATHWAY state suppresses a renewal surface. */
export const NO_PATHWAY_NEUTRAL_MESSAGE =
  "No renewal pathway available in this jurisdiction. No action required at this time.";
